import { MarquisesParams } from "../../constants/constants";
import { AreaType, RectName } from "../../interfaces/IAreaType";
import { ElementSide, Montage } from "../../interfaces/IElementSide";
import { IMarquiseLine } from "../../interfaces/IMarquiseLine.interface";
import { IPoint } from "../../interfaces/IPoint";
import { ChosenMarquiseService } from "../../services/chosen-elements/chosen-marquise.service";
import { ProjectService } from "../../services/project.service";
import { Color } from "../colors/color.model";
import { Glass } from "../glasses/glass.model";
import { MarquiseFront } from "../marquises/marquise-front.model";
import { MarquiseLeft } from "../marquises/marquise-left.model";
import { MarquiseRear } from "../marquises/marquise-rear.model";
import { MarquiseRight } from "../marquises/marquise-right.model";
import { MarquiseTop } from "../marquises/marquise-top.model";
import { MarquiseVertical } from "../marquises/marquise-vertical.model";
import { MarquisesEngine } from "../marquises/marquises-engine.model";
import { FrameProfile } from "../profiles/frame.model";
import { Profile } from "../profiles/profile.model";
import { ElementType } from "../ToolboxModel";
import { ISize } from "../wizard/wizard-parameter.model";
import { ElementStoreModel } from "./element-store.model";
import { FrameStoreModel } from "./frame-store.model";

export class MarquiseEngineStoreModel extends ElementStoreModel {
	location: ElementSide;

	constructor(e: MarquisesEngine) {
		super(e.id, e.configId, "", false, true);

		this.rectOnRoof = e.rect;
		this.location = e.location;
	}
}

export class MarquiseStoreModel extends ElementStoreModel {
	cover: number;
	initialDepth: number;
	depth: number;
	engine: MarquiseEngineStoreModel;
	extension: number;
	firstGlassId: string;
	lastGlassId: string;
	insertionLength: number;
	type: ElementType;

	mlineOnRoof: IMarquiseLine;
	mlineOnFront: IMarquiseLine;
	mlineOnLeft: IMarquiseLine;
	mlineOnRight: IMarquiseLine;

	handlerId: string;
	leftHandlerId: string;
	leftHandlerCount: number;
	rightHandlerId: string;
	rightHandlerCount: number;
	colorConfigId: string;
	isExpandedBack: boolean;
	isExpandedLeft: boolean;
	isExpandedRight: boolean;

	size: ISize;

	area: AreaType;
	leftFrame: FrameStoreModel;
	rightFrame: FrameStoreModel;
	topFrame: FrameStoreModel;
	montage: Montage;

	constructor(m: MarquiseTop | MarquiseVertical, projectService: ProjectService) {
		super(m.id, m.configId, "", false, m.inOffer);

		this.type = m.type;
		this.engine = new MarquiseEngineStoreModel(m.engine);

		if (m instanceof MarquiseVertical) {
			this.leftHandlerId = m.handler.id;
			this.rightHandlerId = m.handler.id;
			this.firstGlassId = m.leftProfile.id;
			this.lastGlassId = m.rightProfile.id;
			this.area = m.area;
			this.montage = m.montage;
			this.storeLocationAndSize(m);
			if (m.leftProfile.type == ElementType.Frame) {
				this.leftFrame = new FrameStoreModel(m.leftProfile as FrameProfile, m.marquiseInfo.connectorId);
			}
			if (m.rightProfile.type == ElementType.Frame) {
				this.rightFrame = new FrameStoreModel(m.rightProfile as FrameProfile, m.marquiseInfo.connectorId);
			}
			if (m.topFrame) {
				this.topFrame = new FrameStoreModel(m.topFrame, m.marquiseInfo.connectorId);
			}

			switch(m.area) {
				case AreaType.Rear:
				case AreaType.Front:
					this.size = { width: Math.round(m.width), height: Math.round(m.height), depth: undefined };
					break;
				case AreaType.Left:
				case AreaType.Right:
					this.size = { width: undefined, height: Math.round(m.height), depth: Math.round(m.width) };
					break;
			}

            // https://trello.com/c/RiD9gMrM
            // 1. Dla skrzynki o długości <=2m dajemy 2 ucwhyty
            // 2. Dla skrzynki o długości > 2m dajemy 3 uchwyty
			if (this.size.width <= MarquisesParams.HANDLERS_WIDTH_TRESHOLD) {
				this.leftHandlerCount = 2;
				this.rightHandlerCount = 2;
			} else {
				this.leftHandlerCount = 3;
				this.rightHandlerCount = 3;
			}

		} else {

			this.leftHandlerId = m.leftHandler.id;
			this.rightHandlerId = m.rightHandler.id;

			this.cover = m.getCover();
			this.initialDepth = m.getUIDepth();
			this.depth = m.getErpDepth();
			this.extension = Math.round(m.getExtension());
			this.firstGlassId = m.firstGlass.id;
			this.lastGlassId = m.lastGlass.id;
			this.insertionLength = m.insertionLength;

			this.mlineOnFront = m.lineOnFront;
			this.mlineOnRoof = m.lineOnRoof;
			this.mlineOnLeft = m.lineOnLeft;
			this.mlineOnRight = m.lineOnRight;

			this.isExpandedBack = m.isExpandedBack;

			this.size = { width: Math.round(m.getWidth()), depth: Math.round(this.depth), height: undefined };
			this.area = AreaType.None;

            // https://trello.com/c/4eflkWXo
            // Używamy 3 uchwytów do głębokości dachu 4500mm a powyżej dokładamy jeden uchwyt dodatkowo.            
			if (projectService.template.depth <= MarquisesParams.HANDLERS_DEPTH_TRESHOLD) {
				this.leftHandlerCount = 3;
				this.rightHandlerCount = 3;
			} else {
				this.leftHandlerCount = 4;
				this.rightHandlerCount = 4;
			}

			if (m.rightHandler.double === true && m.rightHandler.widthCorrection == 0) {
				// Only handlers on the left are countin to avoid double count for many marquises
				this.rightHandlerCount = 0;				
			}
		}

		if (m.fabric?.id) {
			const c = projectService.template.paletteColors.find(p => p.id == m.fabric.id);
			this.colorConfigId = m.fabric.id;
		}

	}

	public static restore(m: MarquiseStoreModel, projectService: ProjectService, points: IPoint[], double: boolean) {
		const info = projectService.template.marquises.find(q => q.id == m.configId);

		let newMarq: MarquiseTop | MarquiseVertical;

		if (m.type != ElementType.MarquiseVertical) {
			var firstGlass = projectService.profileService.roofElements[ElementType.Glass].find(q => q.id == m.firstGlassId);
			if (!firstGlass) {
				firstGlass = projectService.profileService.roofElements[ElementType.Glass]
					.sort((a, b) => a.rect.x - b.rect.x)
					.find((g: Glass) => g.rect.x > m.mlineOnRoof.x1);
			}

			var lastGlass = projectService.profileService.roofElements[ElementType.Glass].find(q => q.id == m.lastGlassId);
			if (!lastGlass) {
				lastGlass = projectService.profileService.roofElements[ElementType.Glass]
					.sort((a, b) => b.rect.x - a.rect.x)
					.find((g: Glass) => g.rect.x < m.mlineOnRoof.x4);
			}

			if (m.handlerId && !m.leftHandlerId && !m.rightHandlerId) {
				// Old project, should be migrated

				const expanded = info.handlers.find(h => h.widthCorrection <= 0 && h.double === double)?.id;
				const nexpanded = info.handlers.find(h => h.widthCorrection >= 0 && h.double === double)?.id;
				m.leftHandlerId = m.isExpandedLeft ? expanded : nexpanded;
				m.rightHandlerId = m.isExpandedRight ? expanded : nexpanded;
			}

			const leftHandler = info.handlers.find(h => h.id == m.leftHandlerId);
			const rightHandler = info.handlers.find(h => h.id == m.rightHandlerId);

			const rear = projectService.template.getDefaultRear();
			newMarq = new MarquiseTop(rear, m.initialDepth, info, projectService.template.isStandalone, projectService.template.dropAngle, m.type, firstGlass.leftBar, lastGlass.rightBar, leftHandler, rightHandler);
			newMarq.lineOnFront = m.mlineOnFront;
			newMarq.lineOnRoof = m.mlineOnRoof;
			newMarq.lineOnLeft = m.mlineOnLeft;
			newMarq.lineOnRight = m.mlineOnRight;

			newMarq.isExpandedBack = m.isExpandedBack;

			newMarq.calculateMarkers();

			projectService.profileService.roofElements[m.type].push(newMarq);
			projectService.profileService.frontElements[m.type].push(newMarq);
			projectService.profileService.leftSideElements[m.type].push(newMarq);
			projectService.profileService.rightSideElements[m.type].push(newMarq);

			ChosenMarquiseService.setCoordinates(newMarq, 0, projectService)

		} else {
			let leftProfile: Profile;
			let rightProfile: Profile;
			let topFrame: FrameProfile;

			var rf = m[RectName.get(m.area)];
			var rl = m.rectOnLeft ?? m.rectOnRight;
			if (!rf && m.area == AreaType.Front && m.rectOnRoof && m.rectOnLeft) {
				rf = { x: m.rectOnRoof.x, y: rl.y, w: m.rectOnRoof.w, h: rl.h };
			}
			if (m.leftFrame?.rect) { // Frame profile
				leftProfile = FrameStoreModel.restore(m.leftFrame, projectService);
			} else { // column
				leftProfile = projectService.profileService.roofElements[ElementType.Column].find(q => q.id == m.firstGlassId);
				if (!leftProfile) {	
					leftProfile = projectService.profileService.getColumn(m.area, ElementSide.Left, rf.x + 17);
				}
			}

			if (m.rightFrame?.rect) { // Frame profile
				rightProfile = FrameStoreModel.restore(m.rightFrame, projectService);
			} else { // column
				rightProfile = projectService.profileService.roofElements[ElementType.Column].find(q => q.id == m.lastGlassId);
				if (!rightProfile) {
					rightProfile = projectService.profileService.getColumn(m.area, ElementSide.Right, rf.x + rf.w - 17);
				}
			}

			if (m.topFrame) {
				topFrame = FrameStoreModel.restore(m.topFrame, projectService);
			}		

			switch (m.area) {
				case AreaType.Front:
					newMarq = new MarquiseFront(projectService, info, leftProfile, rightProfile, null, AreaType.Front, points, m.rectOnRoof, m.rectOnLeft, m.rectOnRight, m.rectOnFront, m.rectOnRear);
					newMarq.montage = m.montage;
					break;
				case AreaType.Rear:
					newMarq = new MarquiseRear(projectService, info, leftProfile, rightProfile, null, AreaType.Front, points, m.rectOnRoof, m.rectOnLeft, m.rectOnRight, m.rectOnFront, m.rectOnRear);
					newMarq.montage = m.montage;
					break;
				case AreaType.Left:
					newMarq = new MarquiseLeft(projectService, info, leftProfile, rightProfile, topFrame, AreaType.Left, points, m.rectOnRoof, m.rectOnLeft, m.rectOnRight, m.rectOnFront, m.rectOnRear);
					newMarq.montage = m.montage;
					break;
				case AreaType.Right:
					newMarq = new MarquiseRight(projectService, info, leftProfile, rightProfile, topFrame, AreaType.Right, points, m.rectOnRoof, m.rectOnLeft, m.rectOnRight, m.rectOnFront, m.rectOnRear);
					newMarq.montage = m.montage;
					break;
				default:
					break;					
			}

			projectService.profileService.addVertical(newMarq as MarquiseVertical);
		}

		if (newMarq instanceof MarquiseTop) {
			newMarq.leftHandler = info.handlers.find(h => h.id == m.leftHandlerId);
			newMarq.rightHandler = info.handlers.find(h => h.id == m.rightHandlerId);
		} else {
			var hid = m.handlerId ?? "";
			if (hid.length == 0) {
				hid = m.leftHandlerId ?? "";
			}
			if (hid.length == 0) {
				hid = m.rightHandlerId ?? "";
			}
			newMarq.handler = info.handlers.find(h => h.id == hid);
		}


		const engine = info.engines.find(e => e.id == m.engine.configId);
		newMarq.engine.setNewEngineParameters(engine);
		newMarq.engine.rect = m.engine.rectOnRoof;
		newMarq.engine.location = m.engine.location;

		const color = projectService.template.paletteColors.find(p => p.id == m.colorConfigId);
		(newMarq.fabric as Color | {}) = color;
		
		newMarq.inOffer = m.inOffer ?? true;
		return newMarq;
	}

}
